import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  Container,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  Pagination,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Close,
  Email,
  WhatsApp,
  Search,
  Category as CategoryIcon,
  QrCode,
  LocalOffer,
  Inventory
} from '@mui/icons-material';
import productService from '../services/productService';
import categoryService from '../services/categoryService';
import contactService from '../services/contactService';
import { useBusiness } from '../context/BusinessContext';
import uploadService from '../services/uploadService';
import { WHATSAPP_BASE_URL } from '../services/api';
import PublicHeader from '../components/PublicHeader';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import PublicFooter from '../components/PublicFooter';
import './Home.css';
import './Catalog.css';

const getRelatedName = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.name || '';
};

const formatProductPrice = (value) => {
  const price = Number(value);
  return Number.isFinite(price)
    ? price.toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    : 'Consultar';
};

const normalizeCatalogProduct = (product) => ({
  ...product,
  brand: getRelatedName(product?.brand),
  category: getRelatedName(product?.category || product?.Category),
  Category: undefined
});

const Catalog = () => {
  const { businessInfo } = useBusiness();
  
  // Estados
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Estados del formulario de consulta
  const [consultaForm, setConsultaForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [consultaMessage, setConsultaMessage] = useState({ type: '', text: '' });
  const [sendingConsulta, setSendingConsulta] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Resetear a página 1 cuando cambian los filtros
    setCurrentPage(1);
  }, [selectedCategory, searchTerm]);

  useEffect(() => {
    // Cargar productos cuando cambia la página o los filtros
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedCategory, searchTerm]);

  // Debounce para la búsqueda
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const loadCategories = async () => {
    try {
      const categoriesResult = await categoryService.getAllPublic();
      
      if (categoriesResult.success) {
        setCategories(categoriesResult.data || []);
      } else {
        console.error('Error al cargar categorías:', categoriesResult.message);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error en loadCategories:', error);
      setCategories([]);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
    const params = {
        page: currentPage,
        limit: 20
      };
      
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      if (selectedCategory) {
        params.categoryId = selectedCategory;
      }
      
      const productsResult = await productService.getCatalogPublic(params);
      
      if (productsResult.success) {
        setProducts((productsResult.data || []).map(normalizeCatalogProduct));
        
        if (productsResult.pagination) {
          setTotalPages(productsResult.pagination.totalPages || 1);
          setTotalItems(productsResult.pagination.totalItems || 0);
        } else {
          setTotalPages(1);
          setTotalItems(productsResult.data?.length || 0);
        }
      } else {
        console.error('Error al cargar productos:', productsResult.message);
        setProducts([]);
        setTotalPages(1);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error en loadProducts:', error);
      setProducts([]);
      setTotalPages(1);
      setTotalItems(0);
    }
    setLoading(false);
  };

  const handleCategoryFilter = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedCategory('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll al inicio de la página
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleConsultaChange = (e) => {
    const { name, value } = e.target;
    setConsultaForm(prev => ({ ...prev, [name]: value }));
  };

  const handleConsultaSubmit = async (e) => {
    e.preventDefault();
    setSendingConsulta(true);
    setConsultaMessage({ type: '', text: '' });
    
    // Validaciones
    if (consultaForm.message.trim().length < 10) {
      setConsultaMessage({
        type: 'warning',
        text: 'El mensaje debe tener al menos 10 caracteres'
      });
      setSendingConsulta(false);
      return;
    }

    if (consultaForm.message.length > 5000) {
      setConsultaMessage({
        type: 'warning',
        text: 'El mensaje no puede superar los 5000 caracteres'
      });
      setSendingConsulta(false);
      return;
    }

    const result = await contactService.send(consultaForm);
    
    if (result.success) {
      setConsultaMessage({
        type: 'success',
        text: result.message || '¡Gracias por tu consulta! Te contactaremos pronto.'
      });
      setConsultaForm({ name: '', email: '', phone: '', subject: '', message: '' });
      setSelectedProduct(null);
      
      setTimeout(() => {
        setConsultaMessage({ type: '', text: '' });
        setModalOpen(false);
      }, 2000);
    } else {
      setConsultaMessage({
        type: 'error',
        text: result.message || 'Error al enviar el mensaje. Por favor, intenta nuevamente.'
      });
    }
    
    setSendingConsulta(false);
  };

  const handleConsultarProducto = (product) => {
    setSelectedProduct(product);
    setConsultaForm(prev => ({
      ...prev,
      subject: `Consulta sobre: ${product.name}`,
      message: `Hola, quisiera consultar sobre el producto:\n\n${product.name}\nPrecio: $${parseFloat(product.price).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n\n`
    }));
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setConsultaMessage({ type: '', text: '' });
  };

  const handleImageClick = (product) => {
    setSelectedImage(product);
    setImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };

  const filteredProducts = products;

  return (
    <Box className="wine-home">
      <PublicHeader active="cava" />
      <FloatingWhatsApp />

      {/* Sección Catálogo */}
      <Box id="catalogo" className="wine-catalog-page">
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" align="center" gutterBottom className="font-bold">
            Nuestra Cava
              </Typography>
          <Typography variant="body1" align="center" color="text.secondary" paragraph sx={{ mb: 4 }}>
            Explora nuestra selección de productos
              </Typography>

          {/* Búsqueda y Filtros */}
          <Card sx={{ mb: 4 }}>
          <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Buscar productos..."
                    value={searchInput}
                    onChange={handleSearchChange}
                  InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      )
                  }}
                />
              </Grid>
                <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Categoría"
                    value={selectedCategory}
                    onChange={(e) => handleCategoryFilter(e.target.value)}
                  >
                    <MenuItem value="">Todas las categorías</MenuItem>
                    {categories.map(category => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
              
              {/* Contador de resultados */}
              {(searchTerm || selectedCategory) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? 'Buscando...' : `${totalItems} producto${totalItems !== 1 ? 's' : ''} encontrado${totalItems !== 1 ? 's' : ''}`}
                  </Typography>
                  <Button 
                    size="small"
                    onClick={handleClearFilters}
                    startIcon={<Close />}
                  >
                    Limpiar filtros
                  </Button>
                </Box>
              )}
          </CardContent>
        </Card>

        {/* Productos */}
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Preparando nuestra cava...
              </Typography>
            </Box>
          ) : filteredProducts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {products.length === 0 
                  ? 'No hay productos disponibles en este momento' 
                  : 'No se encontraron productos con esos filtros'}
              </Typography>
              {products.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Aún no se han cargado vinos o bebidas en nuestra cava.
                  </Typography>
              )}
              {products.length > 0 && (searchTerm || selectedCategory) && (
                <Button 
                  onClick={handleClearFilters}
                  sx={{ mt: 2 }}
                >
                  Limpiar filtros
                </Button>
              )}
            </Box>
          ) : (
            <>
              <Grid container spacing={3}>
                {filteredProducts.map(product => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                    <Card 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <Box
                        onClick={() => handleImageClick(product)}
                        sx={{
                          width: '100%',
                          height: 250,
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.100',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'grey.200',
                            '& img': {
                              transform: 'scale(1.05)',
                              transition: 'transform 0.3s ease'
                            }
                          }
                        }}
                      >
                        <Box
                          component="img"
                          src={product.image ? uploadService.getImageUrl(product.image) : '/no-image.svg'}
                          alt={product.name}
                          sx={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease'
                          }}
                          onError={(e) => {
                            e.target.src = '/no-image.svg';
                          }}
                        />
                      </Box>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="h3" gutterBottom sx={{ minHeight: '3em' }}>
                            {product.name}
                          </Typography>
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="h5" color="primary.main" className="font-bold">
                              ${parseFloat(product.price).toLocaleString('es-AR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </Typography>
                            {product.stock !== undefined && (
                              product.stock > 0 ? (
                                <Chip label={`Stock: ${product.stock}`} size="small" color="success" sx={{ mt: 1 }} />
                              ) : (
                                <Chip label="Sin stock" size="small" color="error" sx={{ mt: 1 }} />
                              )
                            )}
                          </Box>
                        </Box>
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          sx={{ mt: 2 }}
                          onClick={() => handleConsultarProducto(product)}
                          startIcon={<Email />}
                        >
                          Consultar
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              {/* Paginación */}
              {totalPages > 1 && (
                <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
                  <Pagination 
                    count={totalPages} 
                    page={currentPage} 
                    onChange={(event, page) => handlePageChange(page)}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                  <Typography variant="body2" color="text.secondary">
                    Mostrando {filteredProducts.length} de {totalItems} productos
                  </Typography>
                </Stack>
              )}
            </>
          )}
        </Container>
      </Box>

      {/* Modal de Consulta */}
      <Dialog 
        open={modalOpen} 
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Consultar Producto</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {consultaMessage.text && (
            <Alert severity={consultaMessage.type} sx={{ mb: 3 }}>
              {consultaMessage.text}
            </Alert>
          )}

          {selectedProduct && (
            <>
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {selectedProduct.image && (
                      <Box
                        component="img"
                        src={uploadService.getImageUrl(selectedProduct.image)}
                        alt={selectedProduct.name}
                        sx={{ 
                          width: 80, 
                          height: 80, 
                          objectFit: 'cover', 
                          borderRadius: 1 
                        }}
                      />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {selectedProduct.name}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        ${parseFloat(selectedProduct.price).toLocaleString('es-AR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Botón de WhatsApp destacado */}
              {businessInfo?.phone && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={<WhatsApp />}
                    onClick={() => {
                      const message = `Hola! Quisiera consultar sobre:\n\n*${selectedProduct.name}*\nPrecio: $${parseFloat(selectedProduct.price).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      const phoneNumber = businessInfo.phone.replace(/[^0-9]/g, '');
                      const whatsappUrl = `${WHATSAPP_BASE_URL}${phoneNumber}?text=${encodeURIComponent(message)}`;
                      window.open(whatsappUrl, '_blank');
                    }}
                    sx={{ py: 1.5 }}
                  >
                    Consultar por WhatsApp
                  </Button>
                  <Divider sx={{ my: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      o envianos un email
                    </Typography>
                  </Divider>
                </Box>
              )}
            </>
          )}

          <form id="consulta-form" onSubmit={handleConsultaSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Nombre"
                  name="name"
                  value={consultaForm.name}
                  onChange={handleConsultaChange}
                  disabled={sendingConsulta}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email"
                  name="email"
                  value={consultaForm.email}
                  onChange={handleConsultaChange}
                  disabled={sendingConsulta}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Teléfono (opcional)"
                  name="phone"
                  value={consultaForm.phone}
                  onChange={handleConsultaChange}
                  placeholder="Ej: +54 9 11 1234-5678"
                  disabled={sendingConsulta}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Asunto (opcional)"
                  name="subject"
                  value={consultaForm.subject}
                  onChange={handleConsultaChange}
                  placeholder="Ej: Consulta sobre productos"
                  disabled={sendingConsulta}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={4}
                  label="Mensaje"
                  name="message"
                  value={consultaForm.message}
                  onChange={handleConsultaChange}
                  placeholder="Escribí tu consulta aquí (mínimo 10 caracteres)..."
                  disabled={sendingConsulta}
                  inputProps={{ minLength: 10, maxLength: 5000 }}
                  helperText={`${consultaForm.message.length}/5000 caracteres`}
                />
              </Grid>
        </Grid>

            {!businessInfo?.phone && (
              <>
                <Divider sx={{ my: 3 }} />

                {/* Información de contacto adicional (solo si no hay WhatsApp) */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    También podés contactarnos por:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 1 }}>
                    {businessInfo?.email && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Email />}
                        href={`mailto:${businessInfo.email}`}
                      >
                        {businessInfo.email}
                      </Button>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </form>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} disabled={sendingConsulta}>
            Cancelar
          </Button>
          <Button 
            type="submit"
            form="consulta-form"
            variant="contained"
            disabled={sendingConsulta}
            startIcon={sendingConsulta ? <CircularProgress size={20} /> : <Email />}
          >
            {sendingConsulta ? 'Enviando...' : 'Enviar Consulta'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Detalles del Producto */}
      <Dialog 
        open={imageModalOpen} 
        onClose={handleCloseImageModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Detalles del Producto</Typography>
            <IconButton onClick={handleCloseImageModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedImage && (
            <Grid container spacing={3}>
              {/* Imagen del producto */}
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    width: '100%',
                    height: 300,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    component="img"
                    src={selectedImage.image ? uploadService.getImageUrl(selectedImage.image) : '/no-image.svg'}
                    alt={selectedImage.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.src = '/no-image.svg';
                    }}
                  />
                </Box>
              </Grid>

              {/* Detalles del producto */}
              <Grid item xs={12} md={7}>
                <Box>
                  <Typography variant="h5" gutterBottom className="font-bold">
                    {selectedImage.name}
                  </Typography>

                  {/* Precio */}
                  <Typography variant="h4" color="primary.main" className="font-bold" sx={{ my: 2 }}>
                    {formatProductPrice(selectedImage.price) === 'Consultar'
                      ? 'Consultar precio'
                      : `$${formatProductPrice(selectedImage.price)}`}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {/* Información adicional */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {selectedImage.code && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <QrCode fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          <strong>Código:</strong> {selectedImage.code}
                        </Typography>
                      </Box>
                    )}

                    {getRelatedName(selectedImage.brand) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalOffer fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          <strong>Marca:</strong> {getRelatedName(selectedImage.brand)}
                        </Typography>
                      </Box>
                    )}

                    {getRelatedName(selectedImage.category || selectedImage.Category) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategoryIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          <strong>Categoría:</strong>{' '}
                          {getRelatedName(selectedImage.category || selectedImage.Category)}
                        </Typography>
                      </Box>
                    )}

                    {selectedImage.stock !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Inventory fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          <strong>Stock:</strong>{' '}
                          {selectedImage.stock > 0 ? (
                            <Chip 
                              label={`${selectedImage.stock} unidades`} 
                              size="small" 
                              color="success" 
                              sx={{ ml: 1 }} 
                            />
                          ) : (
                            <Chip 
                              label="Sin stock" 
                              size="small" 
                              color="error" 
                              sx={{ ml: 1 }} 
                            />
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {(selectedImage.varietal || selectedImage.vintage || selectedImage.region || selectedImage.volume || selectedImage.alcoholContent) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Grid container spacing={1.5}>
                        {selectedImage.varietal && <Grid item xs={6}><Typography variant="body2"><strong>Varietal:</strong> {selectedImage.varietal}</Typography></Grid>}
                        {selectedImage.vintage && <Grid item xs={6}><Typography variant="body2"><strong>Añada:</strong> {selectedImage.vintage}</Typography></Grid>}
                        {selectedImage.region && <Grid item xs={12}><Typography variant="body2"><strong>Origen:</strong> {selectedImage.region}</Typography></Grid>}
                        {selectedImage.volume && <Grid item xs={6}><Typography variant="body2"><strong>Presentación:</strong> {selectedImage.volume}</Typography></Grid>}
                        {selectedImage.alcoholContent && <Grid item xs={6}><Typography variant="body2"><strong>Alcohol:</strong> {selectedImage.alcoholContent}% vol.</Typography></Grid>}
                      </Grid>
                    </>
                  )}

                  {/* Descripción */}
                  {selectedImage.description && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box>
                        <Typography variant="subtitle2" gutterBottom className="font-bold">
                          Descripción:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                          {selectedImage.description}
                        </Typography>
                      </Box>
                    </>
                  )}
                  {selectedImage.tastingNotes && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" className="font-bold">Notas de cata</Typography>
                      <Typography variant="body2" color="text.secondary">{selectedImage.tastingNotes}</Typography>
                    </Box>
                  )}
                  {selectedImage.pairing && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" className="font-bold">Maridaje recomendado</Typography>
                      <Typography variant="body2" color="text.secondary">{selectedImage.pairing}</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseImageModal}>
            Cerrar
          </Button>
          {selectedImage && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleCloseImageModal();
                handleConsultarProducto(selectedImage);
              }}
              startIcon={<Email />}
            >
              Consultar Producto
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <PublicFooter />
    </Box>
  );
};

export default Catalog;
